// src/pages/pipeline/PipelineStatusesPage.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Plus, GripVertical, Edit, Trash2, Save, X, 
  Info, Calendar, FileText, CheckCircle, XCircle,
  Clock, Loader as LoaderIcon, AlertCircle
} from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { pipelineStatusesAPI } from '@/services/pipelineStatuses';
import { cn } from '@/lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Icon Options
const ICON_OPTIONS = [
  { value: 'Info', label: 'Info', Icon: Info },
  { value: 'Calendar', label: 'Calendar', Icon: Calendar },
  { value: 'FileText', label: 'Document', Icon: FileText },
  { value: 'CheckCircle', label: 'Check', Icon: CheckCircle },
  { value: 'XCircle', label: 'X Circle', Icon: XCircle },
  { value: 'Clock', label: 'Clock', Icon: Clock },
  { value: 'AlertCircle', label: 'Alert', Icon: AlertCircle },
];

// Color Options
const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'orange', label: 'Orange', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'purple', label: 'Purple', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'green', label: 'Green', className: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'red', label: 'Red', className: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'slate', label: 'Slate', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'amber', label: 'Amber', className: 'bg-amber-100 text-amber-700 border-amber-200' },
];

// Sortable Status Item Component
const SortableStatusItem = ({ status, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const IconComponent = ICON_OPTIONS.find(i => i.value === status.icon_name)?.Icon || Info;
  const colorClass = COLOR_OPTIONS.find(c => c.value === status.color_scheme)?.className;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-4 p-4 bg-white rounded-lg border-2 transition-all',
        isDragging && 'opacity-50 shadow-xl'
      )}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-5 h-5 text-gray-400" />
      </div>

      <div className="flex-1 flex items-center gap-3">
        <Badge className={cn('px-3 py-1 gap-2', colorClass)}>
          <IconComponent className="w-4 h-4" />
          {status.title}
        </Badge>
        
        {status.is_default === 1 && (
          <Badge variant="outline" className="text-xs">Default</Badge>
        )}
      </div>

      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={() => onEdit(status)}>
          <Edit className="w-4 h-4" />
        </Button>
        {status.is_default !== 1 && (
          <Button variant="ghost" size="sm" onClick={() => onDelete(status.id)}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Main Component
const PipelineStatusesPage = () => {
  const { addNotification } = useNotifications();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStatus, setEditingStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    color_scheme: 'blue',
    icon_name: 'Info',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const response = await pipelineStatusesAPI.list();
      if (response.success) {
        setStatuses(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load statuses:', error);
      addNotification('Error', 'Failed to load statuses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = statuses.findIndex(s => s.id === active.id);
      const newIndex = statuses.findIndex(s => s.id === over.id);
      
      const reordered = arrayMove(statuses, oldIndex, newIndex);
      setStatuses(reordered);

      try {
        await pipelineStatusesAPI.reorder(reordered);
        addNotification('Success', 'Order updated successfully', 'success');
      } catch (error) {
        addNotification('Error', 'Failed to update order', 'error');
        fetchStatuses();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      addNotification('Validation Error', 'Title is required', 'error');
      return;
    }

    try {
      if (editingStatus) {
        await pipelineStatusesAPI.update(editingStatus.id, formData);
        addNotification('Success', 'Status updated successfully', 'success');
      } else {
        await pipelineStatusesAPI.create(formData);
        addNotification('Success', 'Status created successfully', 'success');
      }

      resetForm();
      fetchStatuses();
    } catch (error) {
      addNotification('Error', error.message || 'Failed to save status', 'error');
    }
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setFormData({
      title: status.title,
      color_scheme: status.color_scheme,
      icon_name: status.icon_name,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this status?')) return;

    try {
      await pipelineStatusesAPI.delete(id);
      addNotification('Success', 'Status deleted successfully', 'success');
      fetchStatuses();
    } catch (error) {
      addNotification('Error', error.message || 'Failed to delete status', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', color_scheme: 'blue', icon_name: 'Info' });
    setEditingStatus(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoaderIcon className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Pipeline Statuses - Management</title>
      </Helmet>

      <div className="p-6 space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Pipeline Statuses</h1>
            <p className="text-gray-600 mt-1">Manage your project pipeline stages</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {showForm ? 'Cancel' : 'Add Status'}
          </Button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <Card>
            <CardHeader>
              <CardTitle>{editingStatus ? 'Edit Status' : 'Add New Status'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Status Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Contract Review"
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Color</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {COLOR_OPTIONS.map(color => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, color_scheme: color.value }))}
                          className={cn(
                            'p-2 rounded border-2 transition-all',
                            color.className,
                            formData.color_scheme === color.value && 'ring-2 ring-offset-2 ring-primary'
                          )}
                        >
                          {color.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Icon</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {ICON_OPTIONS.map(icon => {
                        const Icon = icon.Icon;
                        return (
                          <button
                            key={icon.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, icon_name: icon.value }))}
                            className={cn(
                              'p-2 rounded border-2 transition-all hover:bg-slate-100',
                              formData.icon_name === icon.value && 'bg-primary text-white hover:bg-primary'
                            )}
                          >
                            <Icon className="w-5 h-5 mx-auto" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    {editingStatus ? 'Update Status' : 'Create Status'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Statuses List */}
        <Card>
          <CardHeader>
            <CardTitle>Current Statuses (Drag to Reorder)</CardTitle>
          </CardHeader>
          <CardContent>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={statuses.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {statuses.map(status => (
                    <SortableStatusItem
                      key={status.id}
                      status={status}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {statuses.length === 0 && (
              <p className="text-center text-gray-500 py-8">No statuses found. Add your first status.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default PipelineStatusesPage;
